import React, { useEffect, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Slide } from '@progress/kendo-react-animation';
import { Notification, NotificationGroup, NotificationGroupProps } from '@progress/kendo-react-notification';
import cn from 'clsx';
import './styles.scss';
import { Logger } from '@utils/logger';
import NotificationService from '@utils/notification';

interface ToastNotificationsProps extends NotificationGroupProps {
    autoHideDuration: number;
}

interface ToastMessage {
    text: string;
    type: ToastMessageType;
    creationTime: number;
}

export type ToastMessageType = 'none' | 'success' | 'error' | 'warning' | 'info';

const ToastNotifications = ({ autoHideDuration }: ToastNotificationsProps) => {
    const [messages, setMessages] = useState<ToastMessage[]>([]);

    const onCloseMessage = (index: number) => {
        const messagesCopy = [...messages];
        messagesCopy.splice(index, 1);
        setMessages(messagesCopy);
    };

    const removeItem = (time: number) => {
        setMessages((prevItems) => prevItems.filter((i) => i.creationTime !== time));
    };

    const addItem = (text: string, type: ToastMessageType, timeout: number) => {
        const creationTime = Date.now();
        const newItem: ToastMessage = { text, type, creationTime };
        setMessages((prevItems) => [...prevItems, newItem]);

        setTimeout(() => {
            removeItem(newItem.creationTime);
        }, timeout);
    };

    const getIcon = (messageType: ToastMessageType) => {
        switch (messageType) {
            case 'success':
            case 'info':
                return ['far', 'thumbs-up'] as IconProp;
            case 'error':
            case 'warning':
                return ['far', 'face-frown-slight'] as IconProp;
            default:
                return ['far', 'thumbs-up'] as IconProp;
        }
    };

    useEffect(() => {
        const subscription = NotificationService.onUpdateNotification().subscribe(
            (msg: { text: string; type: ToastMessageType } | string) => {
                Logger.log('GOT SUBSCRIPTION MESSAGE: ', msg);
                // The object is set here because sometimes the same message should be shown
                if (typeof msg === 'string') {
                    addItem(msg, 'none', autoHideDuration);
                } else {
                    addItem(msg.text, msg.type, msg.type === 'error' ? 6000 : autoHideDuration);
                }
            },
        );
        return () => {
            subscription.unsubscribe();
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    return (
        <NotificationGroup
            style={{
                position: 'absolute',
                top: 30,
                left: '50%',
                transform: 'translateX(-50%)',
            }}
            className="toastContainer"
        >
            <Slide direction="down">
                {messages?.map((messageItem, index) => (
                    <Notification
                        key={`toast-notification-${messageItem.creationTime}`}
                        type={{ style: 'none', icon: false }}
                        className={cn('toast-notification', messageItem?.type)}
                        closable
                        onClose={() => onCloseMessage(index)}
                    >
                        <div>
                            <div className="iconContainer">
                                <FontAwesomeIcon icon={getIcon(messageItem.type)} fontSize={44} />
                            </div>
                            <span>{messageItem.text}</span>
                        </div>
                    </Notification>
                ))}
            </Slide>
        </NotificationGroup>
    );
};

export default ToastNotifications;
